import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { GroupFilterSelect, UNGROUPED_LABEL } from '@/components/form/GroupFilterSelect';
import { SearchBox } from '@/components/form/SearchBox';
import { fetchTestCatalog, fetchTestCatalogGroups, type TestCatalogRow } from '@/api/testCatalog';
import type { ClientsPageMeta } from '@/api/clients';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useGroupOptions } from '@/hooks/useGroupOptions';
import { useFlipAnimation } from '@/hooks/useFlipAnimation';
import { formatBalance } from '@/lib/clientFormat';
import type { StagedTest, WizardState } from './wizardState';

type Row = TestCatalogRow & { active: boolean };

/** Та же пауза, что и у пересортировки в LibraryTestsPanel — успеть увидеть сам клик до того, как строка исчезнет. */
const ADD_DELAY_MS = 350;
/**
 * Пауза перед реальным удалением строки из "Выбрано" — ровно на время схлопывания
 * (см. requestRemoveFromSelected). Без неё React убирает <li> из DOM в тот же кадр,
 * что и клик: высота блока "Выбрано" схлопывается мгновенно, а остальные строки
 * внутри него ещё едут вверх через FLIP (0.4s) — снаружи блока (поиск/фильтр,
 * каталог ниже) уже стоят на конечном месте и визуально "обгоняют"/наезжают на
 * ещё уезжающую строку.
 */
const REMOVE_DELAY_MS = 220;

/**
 * Шаг 4 — та же вёрстка, что LibraryTestsPanel в ClientDetailPage.tsx, но
 * данные из общего каталога (без account id, аккаунта ещё нет — см. докблок
 * TestCatalogController в Laravel), а тумблер меняет только локальное
 * состояние мастера (selectedTests), без сетевого вызова. Реальная активация
 * тестов в MT1 произойдёт после создания аккаунта (см. useSubmitWizard.ts).
 *
 * Каталог здесь НЕ пересортировывает строки при переключении (в отличие от
 * LibraryTestsPanel, где это оправдано серверной сортировкой ДО пагинации) —
 * отмеченные тесты и так собраны отдельным блоком "Выбрано" над каталогом,
 * видны сразу независимо от страницы, и в самом каталоге больше не
 * показываются повторно (уже включёнными) — переезжают туда. Переезд не
 * мгновенный: тумблер включается сразу, а строка пропадает из каталога и
 * появляется в "Выбрано" только через ADD_DELAY_MS — иначе клик и исчезновение
 * строки сливаются в один резкий скачок.
 */
export function StepTests({
  state,
  onChange,
}: {
  state: WizardState;
  onChange: (state: WizardState) => void;
}) {
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [group, setGroup] = useState('');
  const [page, setPage] = useState(1);
  const { groups, ungroupedValue } = useGroupOptions(() => fetchTestCatalogGroups('library'), []);
  const [tests, setTests] = useState<Row[] | null>(null);
  const [meta, setMeta] = useState<ClientsPageMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Тесты, по которым уже кликнули, но переезд в "Выбрано" ещё не подтверждён
  // (идёт ADD_DELAY_MS) — тумблер уже включён, а из каталога строка ещё не
  // исчезла. Повторный клик за это время отменяет добавление. Значение — не
  // просто id, а весь StagedTest: нужен целиком, если придётся зафиксировать
  // добавление досрочно (см. эффект ниже).
  const [pendingAdds, setPendingAdds] = useState<Map<number, StagedTest>>(new Map());
  const pendingTimers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  // Строки "Выбрано", уже кликнутые на удаление, но ещё не убранные из
  // selectedTests — идёт схлопывание высоты (см. requestRemoveFromSelected).
  const [removingIds, setRemovingIds] = useState<Set<number>>(new Set());
  const removingTimers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const removingRowRefs = useRef(new Map<number, HTMLLIElement>());
  const removingIdsRef = useRef(removingIds);
  removingIdsRef.current = removingIds;

  // Читается внутри .then() ниже вместо state.selectedTests напрямую — сам
  // fetch-эффект намеренно не зависит от selectedTests (иначе toggle
  // перезапускал бы его на каждый клик), но замыкание .then() создаётся
  // ОДИН раз на комбинацию [search, group, page] и держит state той сборки —
  // при возврате на уже посещённую страницу после переключения теста на
  // ДРУГОЙ странице (эффект пересоздаётся, т.к. page меняется, но порядок
  // событий не гарантирует, что React успел перерендерить со свежим state
  // ДО того, как .then() отработает) активность могла посчитаться по
  // устаревшему selectedTests. Ref всегда актуален независимо от того, когда
  // именно резолвится промис.
  const selectedTestsRef = useRef(state.selectedTests);
  selectedTestsRef.current = state.selectedTests;

  // Нужны и в эффекте размонтирования ниже (замыкание которого создаётся
  // ОДИН раз при монтировании, деп-массив []), и в отложенном коммите — оба
  // места должны видеть самые свежие state/onChange, а не те, что были на
  // момент клика по тумблеру.
  const stateRef = useRef(state);
  stateRef.current = state;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const pendingAddsRef = useRef(pendingAdds);
  pendingAddsRef.current = pendingAdds;

  useEffect(() => {
    const addTimers = pendingTimers.current;
    const removeTimers = removingTimers.current;
    return () => {
      addTimers.forEach((timer) => clearTimeout(timer));
      addTimers.clear();
      removeTimers.forEach((timer) => clearTimeout(timer));
      removeTimers.clear();
      // Уход со шага (напр. сразу "Далее" после клика) раньше отменённого
      // таймера просто отбрасывал ещё не подтверждённое добавление — тумблер
      // пользователь уже видел включённым, а выбор тихо пропадал. Аналогично
      // для удаления — строка уже визуально схлопывается, но ещё числится в
      // selectedTests, пока не сработает свой таймер. Вместо отбрасывания
      // фиксируем всё, что осталось "в подвешенном" состоянии, сразу.
      if (pendingAddsRef.current.size > 0 || removingIdsRef.current.size > 0) {
        const nextSelected = new Map(stateRef.current.selectedTests);
        pendingAddsRef.current.forEach((test, id) => nextSelected.set(id, test));
        removingIdsRef.current.forEach((id) => nextSelected.delete(id));
        onChangeRef.current({ ...stateRef.current, selectedTests: nextSelected });
      }
    };
  }, []);

  // Новый поиск/фильтр — начинаем с первой страницы.
  useEffect(() => {
    setPage(1);
  }, [search, group]);

  useEffect(() => {
    let cancelled = false;
    setTests(null);
    fetchTestCatalog('library', { search: search || undefined, group: group || undefined, page })
      .then(({ items, meta: pageMeta }) => {
        if (cancelled) return;
        setTests(items.map((r) => ({ ...r, active: selectedTestsRef.current.has(r.id) })));
        setMeta(pageMeta);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить каталог тестов.');
      });
    return () => {
      cancelled = true;
    };
  }, [search, group, page]);

  const selected = useMemo(() => Array.from(state.selectedTests.values()), [state.selectedTests]);
  const selectedIds = useMemo(() => selected.map((t) => t.id), [selected]);
  // FLIP тут нужен только блоку "Выбрано" — при удалении оттуда оставшиеся
  // строки подъезжают наверх. Каталог ниже больше не переставляет строки.
  const { setItemRef: setSelectedItemRef, resync: resyncSelectedPositions } = useFlipAnimation(selectedIds);

  const removeFromSelected = (id: number) => {
    const nextSelected = new Map(stateRef.current.selectedTests);
    nextSelected.delete(id);
    onChangeRef.current({ ...stateRef.current, selectedTests: nextSelected });
    setTests((prev) => prev?.map((t) => (t.id === id ? { ...t, active: false } : t)) ?? prev);
  };

  /**
   * Схлопывает строку по высоте (замеренной здесь же, инлайновым style —
   * без этого max-height анимировался бы от условного большого числа, и
   * визуальное сжатие пришлось бы на последнюю часть transition, а не на
   * весь REMOVE_DELAY_MS), и только по завершении анимации реально убирает
   * тест из selectedTests. Кладём в removingIds сразу — переключатель
   * блокируется и выглядит выключенным уже в момент клика.
   */
  const requestRemoveFromSelected = (id: number) => {
    if (removingIds.has(id)) return;
    const el = removingRowRefs.current.get(id);
    if (el) {
      const height = el.getBoundingClientRect().height;
      el.style.maxHeight = `${height}px`;
      el.style.overflow = 'hidden';
      // Форсируем reflow — иначе браузер применит следующий maxHeight сразу,
      // без анимации (тот же приём, что и в useFlipAnimation).
      el.getBoundingClientRect();
      requestAnimationFrame(() => {
        el.style.transition = 'max-height 0.2s ease, opacity 0.2s ease, padding 0.2s ease';
        el.style.maxHeight = '0px';
        el.style.opacity = '0';
        el.style.paddingTop = '0px';
        el.style.paddingBottom = '0px';
      });
    }
    setRemovingIds((prev) => new Set(prev).add(id));
    const timer = setTimeout(() => {
      removingTimers.current.delete(id);
      // CSS-схлопывание уже отработало и сдвинуло соседние строки чисто
      // reflow'ом, не через FLIP (id ещё не убирался из selectedTests) — без
      // resync() следующий реордер (сама реальная мутация в removeFromSelected
      // ниже) сравнил бы текущие позиции с устаревшим "до схлопывания"
      // снимком и на мгновение отбросил бы строки назад (см. докблок
      // useFlipAnimation.resync()).
      resyncSelectedPositions();
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      removeFromSelected(id);
    }, REMOVE_DELAY_MS);
    removingTimers.current.set(id, timer);
  };

  const toggleFromCatalog = (test: Row) => {
    if (pendingAdds.has(test.id)) {
      // Отменяем ещё не подтверждённое добавление — тумблер выключается сразу.
      const timer = pendingTimers.current.get(test.id);
      if (timer) clearTimeout(timer);
      pendingTimers.current.delete(test.id);
      setPendingAdds((prev) => {
        const next = new Map(prev);
        next.delete(test.id);
        return next;
      });
      return;
    }

    const staged: StagedTest = {
      id: test.id,
      name: test.name,
      category: test.category,
      version: test.version,
      basePrice: test.basePrice,
    };
    setPendingAdds((prev) => new Map(prev).set(test.id, staged));
    const timer = setTimeout(() => {
      pendingTimers.current.delete(test.id);
      setPendingAdds((prev) => {
        const next = new Map(prev);
        next.delete(test.id);
        return next;
      });
      const nextSelected = new Map(stateRef.current.selectedTests);
      nextSelected.set(test.id, staged);
      onChangeRef.current({ ...stateRef.current, selectedTests: nextSelected });
      setTests((prev) => prev?.map((t) => (t.id === test.id ? { ...t, active: true } : t)) ?? prev);
    }, ADD_DELAY_MS);
    pendingTimers.current.set(test.id, timer);
  };

  // Уже отмеченные тесты видны в блоке "Выбрано" выше — повторно показывать
  // их же в каталоге незачем. Тест с ещё не подтверждённым добавлением
  // (pendingAdds) остаётся видимым — тумблер уже включён, но строка ещё не переехала.
  const availableTests = tests?.filter((t) => !t.active) ?? null;

  return (
    <div>
      {selected.length > 0 && (
        <div className="card selected-tests">
          <div className="selected-tests__header">Выбрано: {selected.length}</div>
          <ul className="test-list">
            {selected.map((test) => {
              const removing = removingIds.has(test.id);
              const flipRef = setSelectedItemRef(test.id);
              return (
                <li
                  key={test.id}
                  ref={(el) => {
                    flipRef(el);
                    if (el) removingRowRefs.current.set(test.id, el);
                    else removingRowRefs.current.delete(test.id);
                  }}
                  className="test-list__item"
                >
                  <div className="test-list__icon">
                    <Icon name="testChecklist" size={16} />
                  </div>
                  <div className="test-list__body">
                    <div className="test-list__name">
                      {test.name}
                      {test.version && <span className="test-list__version"> · v{test.version}</span>}
                    </div>
                    <div className="test-list__category">
                      {test.category ?? UNGROUPED_LABEL} · базовая цена {formatBalance(test.basePrice)}
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!removing}
                    aria-label={`${test.name}: выбран`}
                    className={`switch${removing ? ' switch--pending' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => requestRemoveFromSelected(test.id)}
                  >
                    <span className="switch__track" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="filter-row">
        <SearchBox value={searchInput} onChange={setSearchInput} />
        <GroupFilterSelect groups={groups} ungroupedValue={ungroupedValue} value={group} onChange={setGroup} />
      </div>

      {error && <p className="field-error">{error}</p>}

      {availableTests === null ? (
        <div className="panel-loading">
          <Spinner />
        </div>
      ) : availableTests.length === 0 ? (
        <p className="cell-muted">
          {tests?.length ? 'Все тесты на этой странице уже выбраны.' : 'Тесты не найдены.'}
        </p>
      ) : (
        <>
          <ul className="test-list">
            {availableTests.map((test) => {
              const pending = pendingAdds.has(test.id);
              return (
                <li key={test.id} className="test-list__item">
                  <div className="test-list__icon">
                    <Icon name="testChecklist" size={16} />
                  </div>
                  <div className="test-list__body">
                    <div className="test-list__name">
                      {test.name}
                      {test.version && <span className="test-list__version"> · v{test.version}</span>}
                    </div>
                    <div className="test-list__category">
                      {test.category ?? UNGROUPED_LABEL} · базовая цена {formatBalance(test.basePrice)}
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={pending}
                    aria-label={`${test.name}: ${pending ? 'выбран' : 'не выбран'}`}
                    className="switch"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => toggleFromCatalog(test)}
                  >
                    <span className="switch__track" />
                  </button>
                </li>
              );
            })}
          </ul>

          {meta && meta.lastPage > 1 && (
            <div className="table-footer">
              <span className="table-footer__status">
                Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
              </span>
              <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
